import { CommonModule } from "@angular/common";
import { ChangeDetectorRef, Component, OnInit } from "@angular/core";
import { FormsModule } from "@angular/forms";
import {
  Company,
  SharedDataService,
  User,
} from "src/app/dashboard/shared-data.service";

import { OverlayPanelModule } from "primeng/overlaypanel";
import { ButtonModule } from "primeng/button";
import { ActivatedRoute } from "@angular/router";
import { DragulaModule, DragulaService } from "ng2-dragula";
import { Subscription } from "rxjs";
import {
  DataField,
  DataFieldFor,
  DataFieldType,
  DataFieldTypeMappingDisplay,
  DynamicColumn,
} from "./models";
import { CustomDataService } from "./custom-data.service";
import { HotToastService } from "@ngneat/hot-toast";
import { BsModalRef, BsModalService } from "ngx-bootstrap/modal";
import { DeleteConfirmationModalComponent } from "../../modals/delete-confirmation-modal.component";
import { ChipModule } from "primeng/chip";
import { SecureDeleteConfirmationModalComponent } from "../../modals/secure-delete-confirmation-modal.component";
import { LocalStorageService } from "src/app/utils/localstorage.service";
import { ReorderOptionsModalComponent } from "./reorder-options-modal/reorder-options-modal.component";
import { AutoCompleteModule } from "primeng/autocomplete";

@Component({
  selector: "app-client-fields",
  imports: [
    FormsModule,
    CommonModule,
    DragulaModule,
    OverlayPanelModule,
    ButtonModule,
    ChipModule,
    AutoCompleteModule,
  ],
  templateUrl: "./client-contact-fields.component.html",
  styleUrls: ["./client-contact-fields.component.scss"],
})
export class ClientContactFieldsComponent implements OnInit {
  company: Company;
  subs = new Subscription();

  isLoading: boolean = true;
  loadingAddField: boolean = false;

  componentFor: DataFieldFor | null = null;
  DataFieldFor = DataFieldFor;
  DataFieldTypeMappingDisplay = DataFieldTypeMappingDisplay;

  dynamic_columns: DynamicColumn[] = [];
  data: DataField[] = [];
  selectedDataField = {
    name: "",
    select_options: [],
    type: "",
  };
  fieldType: DataFieldType = "";
  fieldName: string = "";
  select_options = [];

  constructor(
    private customDataService: CustomDataService,
    private cdr: ChangeDetectorRef,
    private sharedDataService: SharedDataService,
    private toastr: HotToastService,
    private route: ActivatedRoute,
    private dragulaService: DragulaService,
    private bsModalRef: BsModalRef,
    private modalService: BsModalService,
  ) {
    this.subs.add(
      this.dragulaService.dropModel("FIELDS").subscribe(({ targetModel }) => {
        targetModel.forEach((item, index) => {
          item.order = index + 1;
        });
        this.updateDataFieldsOrder();
      }),
    );
  }

  ngOnInit(): void {
    this.route.data.subscribe(({ componentFor }) => {
      this.componentFor = componentFor;
    });
    this.company = this.sharedDataService.company;
    this.getAllDataFields();
  }

  async updateDataFieldsOrder(): Promise<void> {
    await this.customDataService.updateDataFieldsOrder(this.data);
  }
  async getAllDataFields(): Promise<void> {
    try {
      const { data_fields } = await this.customDataService.getAllDataFields(
        this.componentFor,
      );
      this.data = data_fields;
    } catch {
      this.toastr.error("Something went wrong");
    } finally {
      this.isLoading = false;
    }
  }

  async getDynamicColumns(): Promise<void> {
    const { dynamic_columns } =
      await this.customDataService.getAllDynamicColumns(this.componentFor);
    console.log(dynamic_columns);
    this.dynamic_columns = dynamic_columns;
  }

  formatFieldKey(fieldName: string): string {
    return fieldName
      .replace(/ /g, "_")
      .replace(/([a-z])([A-Z])/g, "$1_$2")
      .toLowerCase();
  }

  async addNewField(): Promise<void> {
    this.loadingAddField = true;
    const newField = {
      key: this.formatFieldKey(this.fieldName),
      type: this.fieldType,
      name: this.fieldName,
      order: this.data.length + 1,
      field_for: this.componentFor,
      select_options: this.select_options,
    };

    try {
      const { new_data_field } =
        await this.customDataService.addNewField(newField);
      this.fieldType = "";
      this.fieldName = "";
      if (this.select_options.length) {
        this.select_options = [];
      }
      this.data = [...this.data, new_data_field];
      this.toastr.success("Field Added successfully!");
    } catch (error) {
      this.toastr.error("Something went wrong...");
    } finally {
      this.loadingAddField = false;
    }
  }

  deleteField(field: DataField): void {
    const initialState = {
      deletionMessage: `Removing this field will remove <strong>ALL the data</strong> in this field
      for <strong>ALL your ${
        this.componentFor === DataFieldFor.Contact ? "contacts" : "clients"
      }</strong
      >! Are you really, really sure you want to remove this field?`,
    };
    this.bsModalRef = this.modalService.show(
      SecureDeleteConfirmationModalComponent,
      {
        initialState,
      },
    );
    this.bsModalRef.content.onClose.subscribe(async (res: boolean) => {
      if (res) {
        try {
          const { message } = await this.customDataService.deleteField(
            field.uuid,
          );
          this.data = this.data.filter((_field) => _field.uuid !== field.uuid);
          this.toastr.success(message);
          console.log(message);
        } catch (error) {
          this.toastr.error("Something went wrong...");
        }
      }
    });
  }

  showFieldOptions(field: DataField, limitCharacters: boolean = true): string {
    if (field.select_options) {
      const optionsString = field.select_options.join(", ");
      if (!limitCharacters) return `${optionsString}.`;

      if (optionsString.length > 15) {
        return optionsString.substring(0, 15) + " ...";
      } else {
        return optionsString;
      }
    }
    return "";
  }

  selectDataField(dataField) {
    this.selectedDataField = { ...dataField };
  }

  unselectDataField() {
    this.selectedDataField = {
      name: "",
      select_options: [],
      type: "",
    };
  }

  async editDataField() {
    this.loadingAddField = true;
    const dataField = this.selectedDataField;
    try {
      const { data_field, message } =
        await this.customDataService.editDataField(dataField);
      this.data = this.data.map((field) =>
        field.uuid === data_field.uuid ? data_field : field,
      );
      this.cdr.detectChanges();
      this.selectedDataField = {
        name: "",
        select_options: [],
        type: "",
      };
      this.toastr.success(message);
    } catch (error) {
      this.toastr.error(error.message);
    } finally {
      this.loadingAddField = false;
    }
  }

  openReorderOptionsModal() {
    const initialState = { options: this.selectedDataField.select_options };
    this.bsModalRef = this.modalService.show(ReorderOptionsModalComponent, {
      initialState,
    });
    this.bsModalRef.content.onClose.subscribe(async (res: string[]) => {
      if (res) {
        this.selectedDataField.select_options = res;
      }
    });
  }

  onBlur(e: Event) {
    const inputElement = e.target as HTMLInputElement;
    const inputValue = inputElement.value;
    if (inputValue) {
      inputElement?.dispatchEvent(
        new KeyboardEvent("keydown", { code: "Enter" }),
      );
    }
  }
}
